import { appRoutePath } from 'src/feature/router/util/app-route-path'

export type TodoChecklistFlagKey = 'rolesCompleted' | 'usersCompleted' | 'skillSetCompleted' | 'expectedRatingCompleted'

type TodoChecklistLabelSet = {
  completed: string
  pending: string
  action: string
}

export type TodoChecklistLabels = {
  roles: TodoChecklistLabelSet
  users: TodoChecklistLabelSet
  skillSet: TodoChecklistLabelSet
  expectedRating: TodoChecklistLabelSet
}

export type TodoChecklistTemplate = {
  flagKey: TodoChecklistFlagKey
  completedLabel: string
  pendingLabel: string
  actionLabel: string
  to?: string
  state?: unknown
}

export type TodoChecklistItem = {
  key: TodoChecklistFlagKey
  completed: boolean
  completedLabel: string
  pendingLabel: string
  actionLabel: string
  to?: string
  state?: unknown
  onAction?: () => void
}

export type TodoChecklistItemOverride = Partial<Pick<TodoChecklistItem, 'actionLabel' | 'to' | 'state' | 'onAction'>>

export type TodoChecklistCompletionMap = Partial<Record<TodoChecklistFlagKey, boolean>>

export const getDisabledTodoActionTooltip = (disabledActionTooltip: string) => disabledActionTooltip

const getTodoChecklistRouteState = (levelName?: string) => levelName ? { levelName } : undefined

export const createTodoChecklistTemplates = ({
  levelId,
  levelName,
  labels,
  includeRoles = false,
}: {
  levelId: string
  levelName?: string
  labels: TodoChecklistLabels
  includeRoles?: boolean
}): TodoChecklistTemplate[] => {
  const rolesPageTarget = {
    to: appRoutePath.myTeamRoles(levelId),
    state: getTodoChecklistRouteState(levelName),
  }

  const templates: TodoChecklistTemplate[] = [
    {
      flagKey: 'rolesCompleted',
      completedLabel: labels.roles.completed,
      pendingLabel: labels.roles.pending,
      actionLabel: labels.roles.action,
      ...rolesPageTarget,
    },
    {
      flagKey: 'skillSetCompleted',
      completedLabel: labels.skillSet.completed,
      pendingLabel: labels.skillSet.pending,
      actionLabel: labels.skillSet.action,
      ...rolesPageTarget,
    },
    {
      flagKey: 'expectedRatingCompleted',
      completedLabel: labels.expectedRating.completed,
      pendingLabel: labels.expectedRating.pending,
      actionLabel: labels.expectedRating.action,
      ...rolesPageTarget,
    },
    {
      flagKey: 'usersCompleted',
      completedLabel: labels.users.completed,
      pendingLabel: labels.users.pending,
      actionLabel: labels.users.action,
      ...rolesPageTarget,
    },
  ]

  return includeRoles ? templates : templates.filter(template => template.flagKey !== 'rolesCompleted')
}

export const buildTodoChecklistItems = ({
  templates,
  completionMap,
  allCompleted,
  overrides,
}: {
  templates: TodoChecklistTemplate[]
  completionMap?: TodoChecklistCompletionMap
  allCompleted: boolean
  overrides?: Partial<Record<TodoChecklistFlagKey, TodoChecklistItemOverride>>
}) => {
  return templates.map(template => ({
    key: template.flagKey,
    completed: allCompleted || Boolean(completionMap?.[template.flagKey]),
    completedLabel: template.completedLabel,
    pendingLabel: template.pendingLabel,
    actionLabel: template.actionLabel,
    to: template.to,
    state: template.state,
    ...overrides?.[template.flagKey],
  }))
}
